import { Type, Transform } from 'class-transformer';
import {
  IsInt,
  IsNumber,
  IsIn,
  IsArray,
  IsString,
  IsDateString,
  IsUUID,
  IsNotEmpty,
  ValidateNested,
  IsOptional,
  IsBoolean,
} from 'class-validator';
import { injectable } from 'inversify';

import { HttpService } from '@vk-hr-tek/core/http';
import {
  FiltersResponse,
  Filter,
  FilterService,
  FilterType,
} from '@vk-hr-tek/core/filter';
import { ValidationService } from '@vk-hr-tek/core/validation';
import { UnmarshallerService } from '@vk-hr-tek/core/unmarshaller';
import { QueryService } from '@vk-hr-tek/core/query';

export type FavoritesEntityType = string;

export class DeleteFavoritesParams {
  @IsIn(['news', 'community_post'])
  @IsString()
  @IsNotEmpty()
  entity_type: 'news' | 'community_post';

  @IsUUID()
  @IsNotEmpty()
  entity_id: string;
}

export class AddFavoritesParams {
  @IsIn(['news', 'community_post'])
  @IsString()
  @IsNotEmpty()
  entity_type: 'news' | 'community_post';

  @IsUUID()
  @IsNotEmpty()
  entity_id: string;
}

export type FavoritesTopType = string;

export class GetFavoritesTopListQuery {
  @IsInt()
  @IsOptional()
  limit?: number;

  @IsIn(['all', 'news', 'community_posts'])
  @IsString()
  @IsOptional()
  type?: 'all' | 'news' | 'community_posts';
}

export type CommunityType = string;

export class AccountItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;
}

export class CommunityInfo {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @ValidateNested()
  @Type(() => AccountItem)
  @IsNotEmpty()
  account: AccountItem;

  @IsString()
  @IsNotEmpty()
  title: string;

  @IsString()
  @IsNotEmpty()
  slug: string;

  @IsUUID()
  @IsOptional()
  avatar_attachment_id?: string;

  @IsIn(['public', 'private', 'hidden'])
  @IsString()
  @IsNotEmpty()
  community_type: 'public' | 'private' | 'hidden';

  @IsBoolean()
  @IsNotEmpty()
  comments_enabled: boolean;

  @IsBoolean()
  @IsNotEmpty()
  reactions_enabled: boolean;
}

export class FavoritesTopListPostData {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  title: string;

  @ValidateNested()
  @Type(() => CommunityInfo)
  @IsNotEmpty()
  community_info: CommunityInfo;
}

export class FavoritesTopListNewsData {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  title: string;

  @IsUUID()
  @IsNotEmpty()
  cover_att_id: string;
}

export class FavoritesTopListResponse {
  @ValidateNested({ each: true })
  @Type(() => FavoritesTopListNewsData)
  @IsArray()
  @IsOptional()
  news_list?: FavoritesTopListNewsData[];

  @ValidateNested({ each: true })
  @Type(() => FavoritesTopListPostData)
  @IsArray()
  @IsOptional()
  posts_list?: FavoritesTopListPostData[];
}

export class FeedPostUserActions {
  @IsBoolean()
  @IsOptional()
  favorites?: boolean;
}

export class Reaction {
  @IsString()
  @IsNotEmpty()
  type: string;
}

export class ReactionItem {
  @ValidateNested()
  @Type(() => Reaction)
  @IsNotEmpty()
  reaction: Reaction;

  @IsInt()
  @IsNotEmpty()
  count: number;
}

export class EntityReactions {
  @ValidateNested({ each: true })
  @Type(() => ReactionItem)
  @IsArray()
  @IsNotEmpty()
  data: ReactionItem[];

  @IsString()
  @IsOptional()
  active_reaction_type?: string;
}

export type CryptoProPluginOpenType = string;

export class CompanyItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsOptional()
  tsp_url?: string;

  @IsIn(['capi', 'cades'])
  @IsString()
  @IsOptional()
  crypto_pro_plugin_open_type?: 'capi' | 'cades';

  @IsIn(['api', 'browser_plugin'])
  @IsString()
  @IsOptional()
  hash_source?: 'api' | 'browser_plugin';
}

export class SocialUserCompanyPosition {
  @ValidateNested()
  @Type(() => CompanyItem)
  @IsNotEmpty()
  company: CompanyItem;

  @IsString()
  @IsNotEmpty()
  position: string;

  @IsString()
  @IsOptional()
  unit_name?: string;
}

export class SocialUser {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  first_name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  second_name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  middle_name: string;

  @ValidateNested({ each: true })
  @Type(() => SocialUserCompanyPosition)
  @IsArray()
  @IsOptional()
  companies?: SocialUserCompanyPosition[];
}

export class ContentSurveyActions {
  @IsBoolean()
  @IsNotEmpty()
  results: boolean;

  @IsBoolean()
  @IsNotEmpty()
  report: boolean;
}

export class UserAnswerMultipleTextChoice {
  @IsIn(['multiple_text_choice'])
  @IsString()
  @IsNotEmpty()
  answer_type: 'multiple_text_choice';

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  selected_options: string[];
}

export class UserAnswerSingleTextChoice {
  @IsIn(['single_text_choice'])
  @IsString()
  @IsNotEmpty()
  answer_type: 'single_text_choice';

  @IsUUID()
  @IsOptional()
  selected_option?: string;
}

export class UserAnswerFreeText {
  @IsIn(['free_text'])
  @IsString()
  @IsNotEmpty()
  answer_type: 'free_text';

  @IsString()
  @IsNotEmpty()
  text: string;
}

export class ContentSurveyUserAnswerOneOf {
  @IsNotEmpty()
  object:
    | UserAnswerFreeText
    | UserAnswerSingleTextChoice
    | UserAnswerMultipleTextChoice;
}

export class ContentSurveyUserAnswer {
  @IsUUID()
  @IsNotEmpty()
  question_id: string;

  @ValidateNested()
  @Type(() => ContentSurveyUserAnswerOneOf)
  @IsOptional()
  user_answer?: ContentSurveyUserAnswerOneOf;
}

export class ContentSurveyUserPassing {
  @ValidateNested({ each: true })
  @Type(() => ContentSurveyUserAnswer)
  @IsArray()
  @IsNotEmpty()
  answers: ContentSurveyUserAnswer[];

  @IsInt()
  @IsNotEmpty()
  version: number;
}

export class FreeTextAnswerSettings {
  @IsIn(['free_text'])
  @IsString()
  @IsNotEmpty()
  answer_type: 'free_text';

  @IsInt()
  @IsOptional()
  max_length?: number;

  @IsInt()
  @IsOptional()
  min_length?: number;
}

export class TextChoiceAnswerOption {
  @IsUUID()
  @IsOptional()
  id?: string;

  @IsIn(['text'])
  @IsString()
  @IsNotEmpty()
  type: 'text';

  @IsString()
  @IsNotEmpty()
  text_value: string;
}

export class MultipleTextChoiceAnswerSettings {
  @IsIn(['multiple_text_choice'])
  @IsString()
  @IsNotEmpty()
  answer_type: 'multiple_text_choice';

  @ValidateNested({ each: true })
  @Type(() => TextChoiceAnswerOption)
  @IsArray()
  @IsNotEmpty()
  answer_options: TextChoiceAnswerOption[];
}

export class SingleTextChoiceAnswerSettings {
  @IsIn(['single_text_choice'])
  @IsString()
  @IsNotEmpty()
  answer_type: 'single_text_choice';

  @ValidateNested({ each: true })
  @Type(() => TextChoiceAnswerOption)
  @IsArray()
  @IsNotEmpty()
  answer_options: TextChoiceAnswerOption[];
}

export class ContentSurveyQuestionAnswerSettings {
  @IsNotEmpty()
  object:
    | SingleTextChoiceAnswerSettings
    | MultipleTextChoiceAnswerSettings
    | FreeTextAnswerSettings;
}

export type AttachmentItemType = string;

export class AttachmentItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsIn(['image', 'file'])
  @IsString()
  @IsNotEmpty()
  type: 'image' | 'file';
}

export class BaseContentItem {
  @IsString()
  @IsOptional()
  data?: string;

  @ValidateNested({ each: true })
  @Type(() => AttachmentItem)
  @IsArray()
  @IsOptional()
  attachments?: AttachmentItem[];
}

export class ContentSurveyQuestion {
  @IsUUID()
  @IsOptional()
  id?: string;

  @ValidateNested()
  @Type(() => BaseContentItem)
  @IsNotEmpty()
  content: BaseContentItem;

  @ValidateNested()
  @Type(() => ContentSurveyQuestionAnswerSettings)
  @IsNotEmpty()
  answer_settings: ContentSurveyQuestionAnswerSettings;
}

export class ContentSurvey {
  @IsUUID()
  @IsOptional()
  id?: string;

  @IsString()
  @IsNotEmpty()
  title: string;

  @IsBoolean()
  @IsNotEmpty()
  anonymous: boolean;

  @IsBoolean()
  @IsNotEmpty()
  show_result: boolean;

  @IsDateString()
  @IsOptional()
  completed_at?: string;

  @IsInt()
  @IsNotEmpty()
  version: number;

  @ValidateNested({ each: true })
  @Type(() => ContentSurveyQuestion)
  @IsArray()
  @IsNotEmpty()
  questions: ContentSurveyQuestion[];
}

export class ContentSurveyItem {
  @ValidateNested()
  @Type(() => ContentSurvey)
  @IsNotEmpty()
  survey: ContentSurvey;

  @IsInt()
  @IsOptional()
  passes_num?: number;

  @ValidateNested()
  @Type(() => ContentSurveyUserPassing)
  @IsOptional()
  passing?: ContentSurveyUserPassing;

  @ValidateNested()
  @Type(() => ContentSurveyActions)
  @IsOptional()
  actions?: ContentSurveyActions;
}

export class MentionItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsIn(['user', 'unit'])
  @IsString()
  @IsNotEmpty()
  type: 'user' | 'unit';
}

export class ContentItem {
  @IsString()
  @IsOptional()
  data?: string;

  @ValidateNested({ each: true })
  @Type(() => AttachmentItem)
  @IsArray()
  @IsOptional()
  attachments?: AttachmentItem[];

  @ValidateNested({ each: true })
  @Type(() => MentionItem)
  @IsArray()
  @IsOptional()
  mentions?: MentionItem[];

  @ValidateNested({ each: true })
  @Type(() => ContentSurveyItem)
  @IsArray()
  @IsOptional()
  surveys?: ContentSurveyItem[];
}

export class FeedPostData {
  @IsIn(['post'])
  @IsString()
  @IsNotEmpty()
  type: 'post';

  @IsUUID()
  @IsNotEmpty()
  id: string;

  @ValidateNested()
  @Type(() => CommunityInfo)
  @IsNotEmpty()
  community: CommunityInfo;

  @IsString()
  @IsNotEmpty()
  title: string;

  @IsUUID()
  @IsOptional()
  cover_attachment_id?: string;

  @IsIn(['summary', 'extended'])
  @IsString()
  @IsNotEmpty()
  content_type: 'summary' | 'extended';

  @IsString()
  @IsOptional()
  summary?: string;

  @ValidateNested()
  @Type(() => ContentItem)
  @IsOptional()
  content?: ContentItem;

  @IsBoolean()
  @IsNotEmpty()
  comments_enabled: boolean;

  @IsBoolean()
  @IsNotEmpty()
  reactions_enabled: boolean;

  @IsString({ each: true })
  @IsArray()
  @IsNotEmpty()
  tags: string[];

  @ValidateNested()
  @Type(() => SocialUser)
  @IsOptional()
  author?: SocialUser;

  @IsDateString()
  @IsNotEmpty()
  created_at: string;

  @IsDateString()
  @IsOptional()
  published_at?: string;

  @IsIn(['owner', 'administrator'])
  @IsString()
  @IsOptional()
  edited_by?: 'owner' | 'administrator';

  @ValidateNested()
  @Type(() => EntityReactions)
  @IsOptional()
  reactions?: EntityReactions;

  @IsInt()
  @IsOptional()
  comments_num?: number;

  @IsInt()
  @IsOptional()
  views_num?: number;

  @IsBoolean()
  @IsOptional()
  is_pinned_in_feed?: boolean;

  @ValidateNested()
  @Type(() => FeedPostUserActions)
  @IsOptional()
  actions?: FeedPostUserActions;

  @IsBoolean()
  @IsOptional()
  in_favorites?: boolean;
}

export class FeedNewsUserActions {
  @IsBoolean()
  @IsOptional()
  favorites?: boolean;
}

export class NewsActivityInfo {
  @IsInt()
  @IsNotEmpty()
  views_num: number;

  @IsInt()
  @IsOptional()
  comments_num?: number;

  @IsInt()
  @IsOptional()
  reactions_num?: number;
}

export class Category {
  @IsString()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['gray', 'darkgray', 'green', 'orange', 'purple', 'blue', 'red'])
  @IsString()
  @IsNotEmpty()
  color: 'gray' | 'darkgray' | 'green' | 'orange' | 'purple' | 'blue' | 'red';
}

export class PostsFeedNewsData {
  @IsIn(['news'])
  @IsString()
  @IsNotEmpty()
  type: 'news';

  @IsUUID()
  @IsNotEmpty()
  id: string;

  @ValidateNested()
  @Type(() => AccountItem)
  @IsNotEmpty()
  account: AccountItem;

  @IsDateString()
  @IsNotEmpty()
  published_at: string;

  @IsString()
  @IsNotEmpty()
  title: string;

  @IsString()
  @IsNotEmpty()
  announcement: string;

  @IsUUID()
  @IsNotEmpty()
  cover_att_id: string;

  @ValidateNested()
  @Type(() => Category)
  @IsNotEmpty()
  category: Category;

  @IsString({ each: true })
  @IsArray()
  @IsNotEmpty()
  tags: string[];

  @ValidateNested()
  @Type(() => ContentItem)
  @IsNotEmpty()
  content: ContentItem;

  @ValidateNested()
  @Type(() => SocialUser)
  @IsOptional()
  author?: SocialUser;

  @ValidateNested()
  @Type(() => NewsActivityInfo)
  @IsNotEmpty()
  activity_info: NewsActivityInfo;

  @IsBoolean()
  @IsNotEmpty()
  is_important: boolean;

  @IsBoolean()
  @IsNotEmpty()
  comments_enabled: boolean;

  @IsBoolean()
  @IsNotEmpty()
  reactions_enabled: boolean;

  @ValidateNested()
  @Type(() => EntityReactions)
  @IsOptional()
  reactions?: EntityReactions;

  @IsBoolean()
  @IsOptional()
  is_pinned_in_feed?: boolean;

  @ValidateNested()
  @Type(() => FeedNewsUserActions)
  @IsOptional()
  actions?: FeedNewsUserActions;

  @IsBoolean()
  @IsOptional()
  in_favorites?: boolean;
}

export class FavoritesListResponse {
  @IsArray()
  @IsNotEmpty()
  list: (PostsFeedNewsData | FeedPostData)[];

  @IsInt()
  @IsOptional()
  total?: number;

  @IsInt()
  @IsOptional()
  news_total?: number;

  @IsInt()
  @IsOptional()
  commmunity_posts_total?: number;
}

export class FilterTimeRange {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['timerange'])
  @IsString()
  @IsNotEmpty()
  type: 'timerange';

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsIn(['till_now', 'any', 'from_now'])
  @IsString()
  @IsNotEmpty()
  allow_dates: 'till_now' | 'any' | 'from_now';

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;
}

export class FilterBool {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['bool'])
  @IsString()
  @IsNotEmpty()
  type: 'bool';

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;

  @IsString()
  @IsOptional()
  tooltip?: string;
}

export class FilterMultipleSearch {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['multiple-search'])
  @IsString()
  @IsNotEmpty()
  type: 'multiple-search';

  @IsString()
  @IsNotEmpty()
  url: string;

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;
}

export class FilterSearch {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['search'])
  @IsString()
  @IsNotEmpty()
  type: 'search';

  @IsString()
  @IsNotEmpty()
  url: string;

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsBoolean()
  @IsOptional()
  disable_if_one_option?: boolean;

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;
}

export class FilterText {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['text'])
  @IsString()
  @IsNotEmpty()
  type: 'text';

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;
}

export class FilterSelectBadge {
  @IsString()
  @IsNotEmpty()
  title: string;

  @IsString()
  @IsOptional()
  description?: string;

  @IsIn(['blue', 'velvet', 'red'])
  @IsString()
  @IsOptional()
  color?: 'blue' | 'velvet' | 'red';

  @IsIn(['left', 'bottom'])
  @IsString()
  @IsOptional()
  position?: 'left' | 'bottom';
}

export class FilterSelectOption {
  @IsString()
  @IsNotEmpty()
  value: string;

  @IsString()
  @IsNotEmpty()
  label: string;

  @IsIn(['item'])
  @IsString()
  @IsOptional()
  type?: 'item';

  @ValidateNested()
  @Type(() => FilterSelectBadge)
  @IsOptional()
  badge?: FilterSelectBadge;
}

export class FilterMultiple {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['multiple'])
  @IsString()
  @IsNotEmpty()
  type: 'multiple';

  @ValidateNested({ each: true })
  @Type(() => FilterSelectOption)
  @IsArray()
  @IsNotEmpty()
  options: FilterSelectOption[];

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsBoolean()
  @IsOptional()
  disable_if_one_option?: boolean;

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;

  @IsBoolean()
  @IsOptional()
  autocomplete?: boolean;

  @IsBoolean()
  @IsOptional()
  clearable?: boolean;
}

export class FilterSelect {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['select'])
  @IsString()
  @IsNotEmpty()
  type: 'select';

  @ValidateNested({ each: true })
  @Type(() => FilterSelectOption)
  @IsArray()
  @IsNotEmpty()
  options: FilterSelectOption[];

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsBoolean()
  @IsOptional()
  autocomplete?: boolean;

  @IsBoolean()
  @IsOptional()
  disable_if_one_option?: boolean;

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;
}

export class AvailableFilters {
  @IsArray()
  @IsNotEmpty()
  filters: (
    | FilterSelect
    | FilterMultiple
    | FilterText
    | FilterSearch
    | FilterMultipleSearch
    | FilterBool
    | FilterTimeRange
  )[];
}

export class FavoritesRequestFilters {
  @IsIn(['all', 'news', 'community_posts'])
  @IsString()
  @IsOptional()
  services?: 'all' | 'news' | 'community_posts';
}

export class Pagination {
  @IsInt()
  @IsOptional()
  limit?: number;

  @IsInt()
  @IsOptional()
  offset?: number;
}

export class FavoritesListRequest {
  @ValidateNested()
  @Type(() => Pagination)
  @IsOptional()
  pagination?: Pagination;

  @ValidateNested()
  @Type(() => FavoritesRequestFilters)
  @IsOptional()
  filters?: FavoritesRequestFilters;
}

@injectable()
export class FavoritesService {
  constructor(
    private validator: ValidationService,
    private http: HttpService,
    private unmarshaller: UnmarshallerService,
    private query: QueryService,
  ) {}

  async getFavoritesTopList({
    query = {},
  }: {
    query?: GetFavoritesTopListQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetFavoritesTopListQuery),
    ]);

    let requestPath = '/favorites/top';

    const result = await this.http.get(requestPath, query, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      FavoritesTopListResponse,
    );

    return response;
  }

  async getFavoritesList({
    body = {},
  }: {
    body?: FavoritesListRequest;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(body, FavoritesListRequest),
    ]);

    let requestPath = '/favorites/list';

    const result = await this.http.post(requestPath, body, {
      reloadIfOldVersion: true,
    });

    const response = await this.unmarshaller.unmarshall(
      result,
      FavoritesListResponse,
    );

    return response;
  }

  async getAvailableFavoritesFilters() {
    let requestPath = '/favorites/available_filters';

    const result = await this.http.get(requestPath, {}, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      FiltersResponse,
    );

    return response;
  }

  async deleteFavorites({ params }: { params: DeleteFavoritesParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, DeleteFavoritesParams),
    ]);

    let requestPath = '/favorites/{entity_type}/{entity_id}';

    const paramsKeys = Object.keys(params) as (keyof DeleteFavoritesParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.delete(requestPath, {}, {});

    return result;
  }

  async addFavorites({ params }: { params: AddFavoritesParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, AddFavoritesParams),
    ]);

    let requestPath = '/favorites/{entity_type}/{entity_id}';

    const paramsKeys = Object.keys(params) as (keyof AddFavoritesParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, {}, {});

    return result;
  }
}
